import { useState } from "react";
import { Field, Honeypot, SubmitButton, useSubmit, inputCls, validPhone, validEmail } from "./fields";
import { toast } from "sonner";
import { X, UploadCloud, FileCheck } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import api, { formatApiError } from "@/lib/api";

const INIT = { full_name: "", phone: "", email: "", qualification: "", experience: "", current_location: "", resume_link: "", message: "", website: "" };
const MAX_SIZE = 5 * 1024 * 1024;
const OK_TYPES = ["application/pdf", "application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"];

export default function ApplyModal({ jobs, onClose }) {
  const [f, setF] = useState(INIT);
  const [done, setDone] = useState(false);
  const [resumeFile, setResumeFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const { submit, loading } = useSubmit("/applications", "Application received. Our recruitment team will contact you shortly.");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const pickFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!OK_TYPES.includes(file.type) && !/\.(pdf|doc|docx)$/i.test(file.name)) return toast.error("Please upload a PDF, DOC or DOCX file.");
    if (file.size > MAX_SIZE) return toast.error("File must be under 5 MB.");
    setResumeFile(file);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validPhone(f.phone)) return toast.error("Please enter a valid phone number.");
    if (f.email && !validEmail(f.email)) return toast.error("Please enter a valid email address.");
    let resume_path = "";
    if (resumeFile) {
      setUploading(true);
      try {
        const fd = new FormData();
        fd.append("file", resumeFile);
        const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        resume_path = data.path;
      } catch (err) {
        toast.error(formatApiError(err));
        setUploading(false);
        return;
      }
      setUploading(false);
    }
    const ok = await submit({ ...f, resume_path, job_ids: jobs.map((j) => j.id), job_titles: jobs.map((j) => `${j.title} — ${j.company}`) });
    if (ok) setDone(true);
  };

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[70] bg-darknavy/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        onClick={onClose}
        data-testid="apply-modal-overlay"
      >
        <motion.div
          className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-t-md sm:rounded-sm"
          initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 60, opacity: 0 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          onClick={(e) => e.stopPropagation()}
          data-testid="apply-modal"
        >
          <div className="sticky top-0 bg-darknavy px-6 py-5 flex items-start justify-between">
            <div>
              <p className="text-[11px] uppercase tracking-[0.25em] text-lightgold font-bold mb-1">
                {jobs.length > 1 ? `Applying to ${jobs.length} positions` : "Apply Now"}
              </p>
              <h3 className="text-white font-display text-xl leading-snug pr-6">
                {jobs.map((j) => j.title).join(" · ")}
              </h3>
            </div>
            <button onClick={onClose} data-testid="apply-modal-close" className="text-white/60 hover:text-lightgold transition-colors p-1" aria-label="Close">
              <X size={20} />
            </button>
          </div>

          <div className="p-6 sm:p-8">
            {done ? (
              <div className="text-center py-8" data-testid="apply-success">
                <div className="w-14 h-14 mx-auto mb-5 rounded-full bg-gold/15 flex items-center justify-center">
                  <svg viewBox="0 0 24 24" className="w-7 h-7 text-gold" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                </div>
                <h4 className="font-display text-2xl text-ink mb-2">Application Received</h4>
                <p className="text-muted text-sm max-w-sm mx-auto">Thank you. Your application has been received. Our recruitment team will contact you shortly.</p>
                <p className="text-muted text-xs mt-3">Questions about jobs or careers? <a href="mailto:career@maqlinesolutions.com" className="text-navy font-semibold hover:text-gold transition-colors">career@maqlinesolutions.com</a></p>
                <button onClick={onClose} data-testid="apply-done-close" className="mt-6 text-sm font-semibold text-navy hover:text-gold underline underline-offset-4 transition-colors">Back to jobs</button>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="apply-form">
                <Honeypot value={f.website} onChange={set("website")} />
                <Field label="Full Name" required><input data-testid="af-name" className={inputCls + " w-full"} value={f.full_name} onChange={set("full_name")} required /></Field>
                <Field label="Phone" required><input data-testid="af-phone" className={inputCls + " w-full"} value={f.phone} onChange={set("phone")} required placeholder="+91" /></Field>
                <Field label="Email"><input data-testid="af-email" type="email" className={inputCls + " w-full"} value={f.email} onChange={set("email")} /></Field>
                <Field label="Highest Qualification"><input className={inputCls + " w-full"} value={f.qualification} onChange={set("qualification")} placeholder="e.g. ITI / Diploma / B.Tech" /></Field>
                <Field label="Experience"><input className={inputCls + " w-full"} value={f.experience} onChange={set("experience")} placeholder="e.g. Fresher / 2 years" /></Field>
                <Field label="Current Location"><input className={inputCls + " w-full"} value={f.current_location} onChange={set("current_location")} /></Field>
                <Field label="Upload Resume (PDF / DOC, max 5 MB)" className="sm:col-span-2">
                  <label
                    data-testid="af-resume-upload"
                    className={`flex items-center justify-center gap-3 border border-dashed rounded-sm h-24 cursor-pointer transition-colors duration-300 text-sm ${resumeFile ? "border-gold bg-gold/5 text-ink" : "border-ink/25 text-muted hover:border-gold hover:text-navy"}`}
                  >
                    {resumeFile ? <FileCheck size={20} className="text-gold" /> : <UploadCloud size={20} />}
                    <span className="font-semibold">{resumeFile ? resumeFile.name : "Click to attach your resume"}</span>
                    <input type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={pickFile} />
                  </label>
                  {resumeFile && (
                    <button type="button" onClick={() => setResumeFile(null)} className="text-xs text-muted hover:text-red-500 mt-1.5 transition-colors">Remove file</button>
                  )}
                </Field>
                <Field label="Or Paste a Resume Link" className="sm:col-span-2"><input className={inputCls + " w-full"} value={f.resume_link} onChange={set("resume_link")} placeholder="Google Drive / portfolio link — optional" /></Field>
                <Field label="Message" className="sm:col-span-2"><textarea className={inputCls + " w-full h-20 py-2.5"} value={f.message} onChange={set("message")} placeholder="Anything you'd like us to know" /></Field>
                <div className="sm:col-span-2">
                  <SubmitButton loading={loading || uploading} testId="apply-form-submit">
                    {jobs.length > 1 ? `Submit Application for ${jobs.length} Jobs` : "Submit Application"}
                  </SubmitButton>
                </div>
              </form>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
